import { BrandConfig } from './Reducer';
import {
    BannerMessage,
    TenantUserMetadataSetting,
    PortalSafeDataCollectionLimitResponse,
    BasicTenantSettingsResponse,
    ConsumerTenantFeatureSwitches,
} from '../../Models/Api/strongbox.financialportal';

export enum TenantActions {
    TenantSetBasicSettings = 'Tenant/SET_BASIC_SETTINGS',
    TenantSetBrandConfig = 'Tenant/SET_BRAND_CONFIG',
    TenantOverrideActiveTenant = 'Tenant/SET_ACTIVE_TENANT',
    TenantListEmailDomains = 'Tenant/LIST_EMAIL_DOMAINS',
    TenantListEmailDomainsComplete = 'Tenant/LIST_EMAIL_DOMAINS_COMPLETE',
    TenantListUserMetadataSettings = 'Tenant/LIST_USER_METADATA_SETTINGS',
    TenantListUserMetadataSettingsComplete = 'Tenant/LIST_USER_METADATA_SETTINGS_COMPLETE',
    TenantGetBannerMessages = 'Tenant/GET_BANNER_MESSAGES',
    TenantGetBannerMessagesComplete = 'Tenant/GET_BANNER_MESSAGES_COMPLETE',
    TenantGetDataCollectionLimit = 'Tenant/GET_DATA_COLLECTION_LIMIT',
    TenantGetDataCollectionLimitComplete = 'Tenant/GET_DATA_COLLECTION_LIMIT_COMPLETE',
    TenantGetBasicSettings = 'Tenant/GET_BASIC_SETTINGS',
    TenantGetBasicSettingsComplete = 'Tenant/GET_BASIC_SETTINGS_COMPLETE',
    TenantGetFeatureSwitches = 'Tenant/GET_FEATURE_SWITCHES',
    TenantGetFeatureSwitchesComplete = 'Tenant/GET_FEATURE_SWITCHES_COMPLETE',
}

// Just a couple customer names are listed here under KnownTenants.  There are, of course,
// a whole bunch more.  These are specifically referenced in brandutils.tsx for text
// overrides and thus warrant a constant instead of just magic strings.

export enum KnownTenants {
    defaultLender = 'defaultLender',
    defaultAccountant = 'defaultAccountant',
    strongbox = 'strongbox',
    chase = 'chase',
    chasesandbox = 'chase-sandbox',
    wipfli = 'wipfli',
    mlrpc = 'mlrpc',
    cohencpa = 'cohencpa',
    bmss = 'bmss',
    rsm = 'rsm',
};

export type TenantPaletteItem = {
    name: string,
    page: string,
    value: any,
};

export enum PaletteItemNames {
    h1Login = 'h1-login',
}

export type TenantLineItemWithImage = {
    img: (className?: string) => React.ReactElement;
    text: string;
}

export type TenantLineItemImageSet = {
    lineItems: TenantLineItemWithImage[];
}

export type TenantLoginDestination = {
    id: string;
    name: string;
    url: string;
    tenantName: string;
    // if buttonText is present, this text will be used on the login button for
    // the tenant, otherwise just the name will be used.
    buttonText?: string;
}

export type TenantLoginText = {
    bold?: boolean;
    fontSize?: number; // value in pix
    bottomMargin?: number;  // value in pix
    // Straight up content for a paragraph of text.  There is one special delimiter.
    // If content contains one or more instances of '<team>' that delimiter will 
    // be replaced with the name of the team associated with the portal to which they
    // are signing in.
    content: string;
}

export type MultiTenantSettings = {
    // If defaultLogin is undefined or true, a login button will be shown on
    // the main screen and no selection button will be presented for the team portal
    // to which the user is signing in.  Presumably, some welcome text (see how that's defined)
    // will contain the name of the team to which they're signing in.
    defaultLogin?: boolean;

    welcomeText?: TenantLoginText[];

    // If set, placeLoginAfterWelcomeLine will place the login button after this line
    // from the welcome text.  If this is 0, the button is at the top.   If 
    // defaultLogin is false, this has no effect.
    placeLoginAfterWelcomeLine?: number;

    showSeparatorAfterWelcomeText?: boolean;
    passLoginThrough?: boolean;
    loginDestinations: TenantLoginDestination[];
}

export type TenantFlowDisabledContent = {
    paragraphs: TenantLoginText[];
}

export type TenantDisable = {
    lender?: TenantFlowDisabledContent;
    borrower?: TenantFlowDisabledContent;
}

/**
 * @member { Record<string, string> } redirections - allows for redirecting specific pages under a tenant. 
 * The first value in the record, the key is the pathname, e.g. '/portal' and the second value is the
 * target, e.g. 'notfound'.  In this example portal is redirected to notfound.
 * */

export type TenantBasicSettings = {
    defaultResource: KnownTenants.defaultLender | KnownTenants.defaultAccountant;
    showStrongboxLogo: boolean;
    redirections?: Record<string, string>;
    TOULink?: string;
    StrongboxSecurityInfoLink?: string;
    multiTenantSettings?: MultiTenantSettings;
    tenantDisable?: TenantDisable;
}

export interface IOverrideActiveTenant {
    readonly type: TenantActions.TenantOverrideActiveTenant;
    readonly tenant: string;
}

export interface ISetBasicTenantSettings {
    readonly type: TenantActions.TenantSetBasicSettings;
    readonly settings: TenantBasicSettings;
}

export interface ISetBrandConfig {
    readonly type: TenantActions.TenantSetBrandConfig;
    readonly brandconfig: BrandConfig;
}

export interface IListEmailDomains {
    type: TenantActions.TenantListEmailDomains;
}

export interface IListEmailDomainsComplete {
    type: TenantActions.TenantListEmailDomainsComplete;
    domains: string[];
}

export interface ITenantListUserMetadataSettings {
    type: TenantActions.TenantListUserMetadataSettings;
}

export interface ITenantListUserMetadataSettingsComplete {
    type: TenantActions.TenantListUserMetadataSettingsComplete;
    metadataSettings: TenantUserMetadataSetting[];
}

export interface IGetBannerMessage {
    type: TenantActions.TenantGetBannerMessages;
}

export interface IGetBannerMessageComplete {
    type: TenantActions.TenantGetBannerMessagesComplete;
    messages: BannerMessage[];
}

export interface IGetDataCollectionLimit {
    type: TenantActions.TenantGetDataCollectionLimit;
}

export interface IGetDataCollectionLimitComplete {
    type: TenantActions.TenantGetDataCollectionLimitComplete;
    limits?: PortalSafeDataCollectionLimitResponse;
}

export interface IGetBasicSettings {
    type: TenantActions.TenantGetBasicSettings;
}

export interface IGetBasicSettingsComplete {
    type: TenantActions.TenantGetBasicSettingsComplete;
    settings?: BasicTenantSettingsResponse;
}

export interface IGetFeatureSwitches {
    type: TenantActions.TenantGetFeatureSwitches;
}

export interface IGetFeatureSwitchesComplete {
    type: TenantActions.TenantGetFeatureSwitchesComplete;
    featureSwitches?: ConsumerTenantFeatureSwitches;
}

export type KnownAction =
    IOverrideActiveTenant |
    ISetBasicTenantSettings |
    ISetBrandConfig |
    IListEmailDomains |
    IListEmailDomainsComplete |
    ITenantListUserMetadataSettings |
    ITenantListUserMetadataSettingsComplete |
    IGetBannerMessage |
    IGetBannerMessageComplete |
    IGetDataCollectionLimit |
    IGetDataCollectionLimitComplete |
    IGetBasicSettings |
    IGetBasicSettingsComplete |
    IGetFeatureSwitches |
    IGetFeatureSwitchesComplete;
